import { ClientTable } from "../../../components/ui/ClientTable";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { ContentCard } from "../../../components/ui";
import { InventoryActionToast } from "../components/InventoryActionToast";
import { MarkAsDeliveredModal } from "../components/MarkAsDeliveredModal";
import { RequestRevokeModal } from "../components/RequestRevokeModal";
import { RequestUnlockModal } from "../components/RequestUnlockModal";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";
import {
  canActOnAllocationStatus,
  isDeliveredAllocationStatus,
  isPartiallyAllocatedHeaderStatus,
  isPendingApprovalStatus,
  isUndeliverablePartialLineStatus,
} from "../utils/allocationStatus";

const LOOKUP_PAGE_SIZE = 10;

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function toInputDate(value) {
  if (!value) return "";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function mergeUniqueById(existing, incoming) {
  const map = new Map();
  [...(existing || []), ...(incoming || [])].forEach((item) => {
    if (item?.id == null || item.id === "") return;
    map.set(String(item.id), item);
  });
  return Array.from(map.values());
}

function matchAllocatedBatchKeys(availableBatches, allocatedBatches) {
  const keys = [];
  for (const allocated of allocatedBatches || []) {
    const batchNumber =
      allocated.batchNumber && allocated.batchNumber !== "—"
        ? String(allocated.batchNumber)
        : "";
    const warehouseId =
      allocated.warehouseId != null ? String(allocated.warehouseId) : "";
    const price = Number(allocated.purchasePricePerUnit) || 0;

    const match = (availableBatches || []).find((b) => {
      const sameWh =
        !warehouseId || String(b.warehouseId) === warehouseId;
      const sameBatch =
        !batchNumber ||
        String(b.batchNumber) === batchNumber ||
        (batchNumber === "" && (!b.batchNumber || b.batchNumber === "—"));
      const samePrice =
        !price || Number(b.purchasePricePerUnit) === price;
      return sameWh && sameBatch && samePrice;
    });
    if (match?.key && !keys.includes(match.key)) {
      keys.push(match.key);
    }
  }
  return keys;
}

function seedBatchQtyByKey(availableBatches, allocatedBatches) {
  const qtyByKey = {};
  for (const allocated of allocatedBatches || []) {
    const batchNumber =
      allocated.batchNumber && allocated.batchNumber !== "—"
        ? String(allocated.batchNumber)
        : "";
    const warehouseId =
      allocated.warehouseId != null ? String(allocated.warehouseId) : "";
    const price = Number(allocated.purchasePricePerUnit) || 0;
    const qty =
      Number(
        allocated.allocatedQty ?? allocated.allocatedQuantity ?? allocated.qty
      ) || 0;

    const match = (availableBatches || []).find((b) => {
      const sameWh =
        !warehouseId || String(b.warehouseId) === warehouseId;
      const sameBatch =
        !batchNumber ||
        String(b.batchNumber) === batchNumber ||
        (batchNumber === "" && (!b.batchNumber || b.batchNumber === "—"));
      const samePrice =
        !price || Number(b.purchasePricePerUnit) === price;
      return sameWh && sameBatch && samePrice;
    });
    if (match?.key) {
      qtyByKey[match.key] = qty;
    }
  }
  return qtyByKey;
}

function isBlockedByPriceStatus(status) {
  const key = String(status || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return key === "BLOCKED" || key === "BLOCKED_BY_PRICE";
}

/**
 * GET may return Eligible after unlock without unlockStatus/unlockedPrice.
 * Trust non-blocked detail status so edit UI does not re-derive Blocked from price.
 */
function inferUnlockStatusFromAllocationDetail(detail) {
  if (!detail) return null;
  const explicit = String(detail.unlockStatus || "").toUpperCase();
  if (
    explicit === "APPROVED" ||
    explicit === "PENDING" ||
    explicit === "REJECTED"
  ) {
    return explicit;
  }
  if (detail.unlockedPrice != null && detail.unlockedPrice !== "") {
    return "APPROVED";
  }
  const detailId =
    detail.customerAllocationLineDetailId ??
    detail.customerAllocationDetailId ??
    detail.id;
  if (
    detailId != null &&
    detailId !== "" &&
    detail.status &&
    !isBlockedByPriceStatus(detail.status)
  ) {
    return "APPROVED";
  }
  return null;
}

function enrichBatchesWithAllocationDetails(availableBatches, allocatedBatches) {
  return (availableBatches || []).map((batch) => {
    const batchNumber =
      batch.batchNumber && batch.batchNumber !== "—"
        ? String(batch.batchNumber)
        : "";
    const warehouseId =
      batch.warehouseId != null ? String(batch.warehouseId) : "";
    const price = Number(batch.purchasePricePerUnit) || 0;

    const match = (allocatedBatches || []).find((allocated) => {
      const allocBatch =
        allocated.batchNumber && allocated.batchNumber !== "—"
          ? String(allocated.batchNumber)
          : "";
      const allocWh =
        allocated.warehouseId != null ? String(allocated.warehouseId) : "";
      const allocPrice = Number(allocated.purchasePricePerUnit) || 0;
      const sameWh = !allocWh || !warehouseId || allocWh === warehouseId;
      const sameBatch =
        !allocBatch ||
        !batchNumber ||
        allocBatch === batchNumber ||
        (allocBatch === "" && batchNumber === "");
      const samePrice = !allocPrice || !price || allocPrice === price;
      return sameWh && sameBatch && samePrice;
    });

    if (!match) return batch;
    return {
      ...batch,
      customerAllocationLineDetailId:
        match.customerAllocationLineDetailId ||
        batch.customerAllocationLineDetailId ||
        "",
      unlockedPrice:
        match.unlockedPrice != null ? match.unlockedPrice : batch.unlockedPrice,
      unlockStatus:
        inferUnlockStatusFromAllocationDetail(match) ||
        batch.unlockStatus ||
        null,
      allocationDetailStatus: match.status || batch.allocationDetailStatus || null,
    };
  });
}

function resolveUnlockStatus(batch, unlockStatusByDetailId = {}) {
  const detailId = batch?.customerAllocationLineDetailId
    ? String(batch.customerAllocationLineDetailId)
    : "";
  if (detailId && unlockStatusByDetailId[detailId]) {
    return String(unlockStatusByDetailId[detailId]).toUpperCase();
  }
  const fromBatch = String(batch?.unlockStatus || "").toUpperCase();
  if (
    fromBatch === "PENDING" ||
    fromBatch === "APPROVED" ||
    fromBatch === "REJECTED"
  ) {
    return fromBatch;
  }
  if (batch?.unlockedPrice != null && batch.unlockedPrice !== "") {
    return "APPROVED";
  }
  return null;
}

function formatCurrency(val) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(val) || 0);
}

function formatDisplayDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Parse API/UI date strings to a comparable timestamp; invalid → null. */
function toSortTime(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const raw = String(value).trim();
  if (!raw) return null;

  const iso = Date.parse(raw);
  if (Number.isFinite(iso)) return iso;

  // DD/MM/YYYY or DD-MM-YYYY
  const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) {
    const t = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1])).getTime();
    return Number.isFinite(t) ? t : null;
  }

  // DD-MMM-YYYY (e.g. 15-Jan-2024)
  const dMonY = raw.match(/^(\d{1,2})[- ]([A-Za-z]{3,})[- ](\d{4})$/);
  if (dMonY) {
    const t = Date.parse(`${dMonY[2]} ${dMonY[1]}, ${dMonY[3]}`);
    return Number.isFinite(t) ? t : null;
  }

  return null;
}

/** Ascending by date (earliest first); missing dates last; stable on ties. */
function compareEarliestFirst(aVal, bVal) {
  const aTime = toSortTime(aVal);
  const bTime = toSortTime(bVal);
  if (aTime == null && bTime == null) return 0;
  if (aTime == null) return 1;
  if (bTime == null) return -1;
  return aTime - bTime;
}

/**
 * Sort available batches by dispatch method — earliest first.
 * FIFO → receipt date; FEFO → warranty end date.
 */
function sortBatches(batches, method) {
  const sorted = [...(batches || [])];
  const dateKey = method === "FEFO" ? "warrantyEndDate" : "receiptDate";
  sorted.sort((a, b) => {
    const byDate = compareEarliestFirst(a[dateKey], b[dateKey]);
    if (byDate !== 0) return byDate;
    // Tie-breakers keep order deterministic across warehouses/batches.
    const byWarehouse = String(a.warehouse || "").localeCompare(String(b.warehouse || ""));
    if (byWarehouse !== 0) return byWarehouse;
    return String(a.batchNumber || "").localeCompare(String(b.batchNumber || ""));
  });
  return sorted;
}

/** Allocate IOF across selected batches in the order the user ticked them. */
function allocateInSelectionOrder(selectedKeys, batches, iofQty, consideredCost) {
  const byKey = new Map((batches || []).map((b) => [b.key, b]));
  let remaining = Math.max(0, Number(iofQty) || 0);
  const allocationByKey = {};
  const batchEntries = [];

  for (const key of selectedKeys || []) {
    const b = byKey.get(key);
    if (!b) continue;
    const eligibility =
      b.eligibility || batchEligibility(b, consideredCost);
    if (eligibility !== "eligible") {
      allocationByKey[key] = 0;
      continue;
    }
    if (remaining <= 0) {
      allocationByKey[key] = 0;
      continue;
    }
    const alloc = Math.min(remaining, Number(b.availableQty) || 0);
    allocationByKey[key] = alloc;
    if (alloc > 0) {
      batchEntries.push({
        key: b.key,
        batchNumber: b.batchNumber,
        warehouseId: b.warehouseId,
        warehouse: b.warehouse,
        purchasePricePerUnit: b.purchasePricePerUnit,
        qty: alloc,
      });
      remaining -= alloc;
    }
  }

  const allocatedQty = batchEntries.reduce((sum, e) => sum + e.qty, 0);
  return { allocationByKey, batchEntries, allocatedQty, remaining };
}

/** Build allocation from user-editable per-batch quantities (selected batches only). */
function buildAllocationFromQtyByKey(
  selectedKeys,
  batches,
  consideredCost,
  qtyByKey
) {
  const byKey = new Map((batches || []).map((b) => [b.key, b]));
  const allocationByKey = {};
  const batchEntries = [];

  for (const key of selectedKeys || []) {
    const b = byKey.get(key);
    if (!b) continue;
    const eligibility =
      b.eligibility || batchEligibility(b, consideredCost);
    if (eligibility !== "eligible") {
      allocationByKey[key] = 0;
      continue;
    }
    const available = Math.max(0, Number(b.availableQty) || 0);
    const raw = Number(qtyByKey?.[key]);
    const qty = Math.max(
      0,
      Math.min(Number.isFinite(raw) ? raw : 0, available)
    );
    allocationByKey[key] = qty;
    if (qty > 0) {
      batchEntries.push({
        key: b.key,
        batchNumber: b.batchNumber,
        warehouseId: b.warehouseId,
        warehouse: b.warehouse,
        purchasePricePerUnit: b.purchasePricePerUnit,
        qty,
      });
    }
  }

  const allocatedQty = batchEntries.reduce((sum, e) => sum + e.qty, 0);
  return { allocationByKey, batchEntries, allocatedQty };
}

/** Suggest qty for a newly selected batch from remaining IOF after other selected qtys. */
function suggestQtyForBatch(
  selectedKeys,
  batches,
  iofQty,
  consideredCost,
  qtyByKey,
  newKey
) {
  const byKey = new Map((batches || []).map((b) => [b.key, b]));
  let used = 0;
  for (const key of selectedKeys || []) {
    if (key === newKey) continue;
    const b = byKey.get(key);
    if (!b) continue;
    const eligibility =
      b.eligibility || batchEligibility(b, consideredCost);
    if (eligibility !== "eligible") continue;
    used += Math.max(0, Number(qtyByKey?.[key]) || 0);
  }
  const remaining = Math.max(0, (Number(iofQty) || 0) - used);
  const batch = byKey.get(newKey);
  if (!batch) return 0;
  return Math.min(remaining, Math.max(0, Number(batch.availableQty) || 0));
}

/** Re-waterfall auto quantities across selected batches (e.g. after IOF change). */
function autoFillQtyByKey(selectedKeys, batches, iofQty, consideredCost) {
  const { allocationByKey } = allocateInSelectionOrder(
    selectedKeys,
    batches,
    iofQty,
    consideredCost
  );
  const next = {};
  for (const key of selectedKeys || []) {
    next[key] = Number(allocationByKey[key]) || 0;
  }
  return next;
}

function batchEligibility(batch, consideredCost, unlockStatus = null) {
  if (
    consideredCost === "" ||
    consideredCost == null ||
    Number.isNaN(Number(consideredCost))
  ) {
    return "awaiting";
  }
  if (Number(batch.purchasePricePerUnit) > Number(consideredCost)) {
    const unlock = String(unlockStatus || "").toUpperCase();
    // Only manager-approved unlock makes the batch allocatable.
    // QUEUED/PENDING stay blocked so the line can become Blocked_by_price on save.
    if (unlock === "APPROVED") {
      return "eligible";
    }
    return "blocked";
  }
  return "eligible";
}

function unlockQueueKey(lineId, batchKey) {
  return `${lineId}::${batchKey}`;
}

/** Pick customerAllocationLineDetailId from a create/GET line (raw or mapped). */
function pickDetailIdFromLine(line, queued = {}) {
  const details = Array.isArray(line?.details)
    ? line.details
    : Array.isArray(line?.batches)
      ? line.batches
      : [];

  const batchNumber =
    queued.batchNumber && queued.batchNumber !== "—"
      ? String(queued.batchNumber)
      : "";
  const warehouseId =
    queued.warehouseId != null ? String(queued.warehouseId) : "";
  const price = Number(queued.purchasePricePerUnit) || 0;

  const matched = details.find((d) => {
    const detailId =
      d.customerAllocationLineDetailId ??
      d.customerAllocationDetailId ??
      d.id;
    if (detailId == null || detailId === "") return false;

    const sameWh =
      !warehouseId || String(d.warehouseId || "") === warehouseId;
    const dBatch = d.batchNumber && d.batchNumber !== "—" ? String(d.batchNumber) : "";
    const sameBatch =
      !batchNumber ||
      dBatch === batchNumber ||
      (batchNumber === "" && !dBatch);
    const samePrice =
      !price || Number(d.purchasePricePerUnit) === price;
    return sameWh && sameBatch && samePrice;
  });

  if (matched) {
    return (
      matched.customerAllocationLineDetailId ??
      matched.customerAllocationDetailId ??
      matched.id
    );
  }

  const first = details.find(
    (d) =>
      d.customerAllocationLineDetailId != null ||
      d.customerAllocationDetailId != null ||
      d.id != null
  );
  if (!first) return null;
  return (
    first.customerAllocationLineDetailId ??
    first.customerAllocationDetailId ??
    first.id
  );
}

/**
 * After create/update: decide unlock action for a queued unlock request.
 * Queued unlock always POSTs when a real detail id exists (even if line is
 * Eligible — priceOverrides force-allocates expensive stock but unlock was
 * still requested). Skip only when there is no detail to unlock.
 */
function resolveQueuedUnlockAction(queued, saveResponse, mappedDetail) {
  const productId = String(queued.productId || "");

  const rawLines = Array.isArray(saveResponse?.lines)
    ? saveResponse.lines
    : Array.isArray(saveResponse?.data?.lines)
      ? saveResponse.data.lines
      : [];

  let rawLine = rawLines.find(
    (l) => String(l.productId ?? l.product_id ?? "") === productId
  );

  if (!rawLine && rawLines.length === 1 && productId) {
    rawLine = rawLines[0];
  }
  if (!rawLine && rawLines.length > 0 && productId) {
    rawLine =
      rawLines.find((l) => isBlockedByPriceStatus(l.status)) || rawLines[0];
  }

  if (rawLine) {
    const status = rawLine.status || "";
    const detailId = pickDetailIdFromLine(rawLine, queued);
    if (detailId != null) {
      return { action: "unlock", status, detailId };
    }
    // No detail row — cannot unlock (line id is not a detail id).
    if (isBlockedByPriceStatus(status)) {
      return { action: "missing_detail", status };
    }
    return { action: "skip", status: status || "Eligible" };
  }

  const products = mappedDetail?.products || [];
  let product = products.find(
    (p) => String(p.productId || "") === productId
  );
  if (!product && products.length === 1 && productId) {
    product = products[0];
  }
  if (!product && products.length > 0) {
    product =
      products.find((p) => isBlockedByPriceStatus(p.status)) || products[0];
  }
  if (!product) {
    return { action: "missing_line", status: null };
  }

  const status = product.status || "";
  const detailId = pickDetailIdFromLine(
    { details: product.batches, batches: product.batches },
    queued
  );
  if (detailId != null) {
    return { action: "unlock", status, detailId };
  }
  if (isBlockedByPriceStatus(status)) {
    return { action: "missing_detail", status };
  }
  return { action: "skip", status: status || "Eligible" };
}

function emptyLine(id) {
  return {
    id,
    customerAllocationLineId: "",
    allocationDetails: [],
    productId: "",
    productName: "",
    iofQty: 0,
    consideredCost: "",
    selectedBatchKeys: [],
    batchQtyByKey: {},
    editingBatches: true,
    batches: [],
    availableQty: 0,
    loadingBatches: false,
    lineStatus: "",
    lineDeliveryDate: "",
    seedAllocatedQty: 0,
  };
}

export function AllocateToCustomerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allocationId } = useParams();
  const isEditMode = Boolean(allocationId);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successToast, setSuccessToast] = useState("");

  const [productsList, setProductsList] = useState([]);
  const [productPage, setProductPage] = useState(0);
  const [productHasMore, setProductHasMore] = useState(false);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const productFetchLock = useRef(false);

  const [customersList, setCustomersList] = useState([]);
  const [customerPage, setCustomerPage] = useState(0);
  const [customerHasMore, setCustomerHasMore] = useState(false);
  const [loadingMoreCustomers, setLoadingMoreCustomers] = useState(false);
  const customerFetchLock = useRef(false);

  const [headerData, setHeaderData] = useState({
    orderType: "Sales",
    date: todayIsoDate(),
    iofNumber: "",
    customerId: "",
    projectName: "",
    invoiceDate: todayIsoDate(),
    deliveryDate: todayIsoDate(),
    dispatchMethod: "FIFO",
  });

  const [lines, setLines] = useState([emptyLine("line-1")]);
  const [formErrors, setFormErrors] = useState({});
  const [formBanner, setFormBanner] = useState("");
  const [allocationDetail, setAllocationDetail] = useState(null);
  const [actionError, setActionError] = useState("");
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [deliverModalOpen, setDeliverModalOpen] = useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [unlockError, setUnlockError] = useState("");
  const [pendingRevokeDetailIds, setPendingRevokeDetailIds] = useState(
    () => new Set()
  );
  const [unlockStatusByDetailId, setUnlockStatusByDetailId] = useState({});
  /** Local unlock requests queued until Save (create / new details). */
  const [queuedUnlocks, setQueuedUnlocks] = useState({});

  const loadProducts = useCallback(async (page, { append = false } = {}) => {
    if (append && productFetchLock.current) return;
    if (!append) productFetchLock.current = false;
    if (productFetchLock.current) return;
    productFetchLock.current = true;
    if (append) setLoadingMoreProducts(true);

    try {
      const result = await inventoryManagementService.getAllocationProducts({
        page,
        size: LOOKUP_PAGE_SIZE,
      });
      setProductsList((prev) =>
        append ? mergeUniqueById(prev, result.content) : result.content || []
      );
      setProductPage(result.page ?? page);
      setProductHasMore(Boolean(result.hasMore));
    } catch (err) {
      console.error("Failed to load allocation products:", err);
      if (!append) setProductsList([]);
      setProductHasMore(false);
      throw err;
    } finally {
      setLoadingMoreProducts(false);
      productFetchLock.current = false;
    }
  }, []);

  const loadCustomers = useCallback(async (page, { append = false } = {}) => {
    if (append && customerFetchLock.current) return;
    if (!append) customerFetchLock.current = false;
    if (customerFetchLock.current) return;
    customerFetchLock.current = true;
    if (append) setLoadingMoreCustomers(true);

    try {
      const result = await inventoryManagementService.getAllocationCustomers({
        page,
        size: LOOKUP_PAGE_SIZE,
      });
      setCustomersList((prev) =>
        append ? mergeUniqueById(prev, result.content) : result.content || []
      );
      setCustomerPage(result.page ?? page);
      setCustomerHasMore(Boolean(result.hasMore));
    } catch (err) {
      console.error("Failed to load allocation customers:", err);
      if (!append) setCustomersList([]);
      setCustomerHasMore(false);
      throw err;
    } finally {
      setLoadingMoreCustomers(false);
      customerFetchLock.current = false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setPendingRevokeDetailIds(new Set());
    setUnlockStatusByDetailId({});
    setQueuedUnlocks({});

    (async () => {
      const [productsResult, customersResult, detailResult, pendingResult] =
        await Promise.allSettled([
          inventoryManagementService.getAllocationProducts({
            page: 0,
            size: LOOKUP_PAGE_SIZE,
          }),
          inventoryManagementService.getAllocationCustomers({
            page: 0,
            size: LOOKUP_PAGE_SIZE,
          }),
          isEditMode
            ? inventoryManagementService.getAllocationDetail(allocationId)
            : Promise.resolve(null),
          isEditMode
            ? inventoryManagementService.getPendingRevokeUnlockRequests()
            : Promise.resolve([]),
        ]);
      if (cancelled) return;

      let nextProducts = [];
      let nextCustomers = [];

      if (productsResult.status === "fulfilled") {
        const products = productsResult.value;
        nextProducts = products.content || [];
        setProductPage(products.page ?? 0);
        setProductHasMore(Boolean(products.hasMore));
      } else {
        console.error("Failed to load allocation products:", productsResult.reason);
        setProductHasMore(false);
      }

      if (customersResult.status === "fulfilled") {
        const customers = customersResult.value;
        nextCustomers = customers.content || [];
        setCustomerPage(customers.page ?? 0);
        setCustomerHasMore(Boolean(customers.hasMore));
      } else {
        console.error("Failed to load allocation customers:", customersResult.reason);
        setCustomerHasMore(false);
      }

      if (isEditMode && detailResult.status === "rejected") {
        console.error("Failed to load allocation for edit:", detailResult.reason);
        setError(t("validation.allocationNotFound"));
        setProductsList(nextProducts);
        setCustomersList(nextCustomers);
        setLoading(false);
        return;
      }

      if (isEditMode && detailResult.status === "fulfilled" && detailResult.value) {
        const detail = detailResult.value;
        setAllocationDetail(detail);

        if (pendingResult.status === "fulfilled") {
          const allocationKey = String(
            detail.header?.allocationId || allocationId || ""
          );
          const pendingIds = new Set();
          const unlockStatuses = {};
          (pendingResult.value || []).forEach((req) => {
            if (String(req.status || "").toUpperCase() !== "PENDING") return;
            if (
              allocationKey &&
              String(req.customerAllocationId || "") !== allocationKey
            ) {
              return;
            }
            const detailId = String(req.customerAllocationDetailId || "");
            if (!detailId) return;
            const actionType = String(req.actionType || "").toUpperCase();
            if (actionType === "UNLOCK") {
              unlockStatuses[detailId] = "PENDING";
            } else {
              pendingIds.add(detailId);
            }
          });

          (detail.products || []).forEach((product) => {
            (product.batches || []).forEach((b) => {
              const detailId = String(b.customerAllocationLineDetailId || "");
              if (!detailId || unlockStatuses[detailId]) return;
              const inferred = inferUnlockStatusFromAllocationDetail(b);
              if (inferred) {
                unlockStatuses[detailId] = inferred;
              }
            });
          });

          setPendingRevokeDetailIds(pendingIds);
          setUnlockStatusByDetailId(unlockStatuses);
        } else {
          console.error(
            "Failed to load pending revoke requests:",
            pendingResult.reason
          );
        }

        const header = detail.header || {};
        const customerId =
          header.customerId != null && header.customerId !== ""
            ? String(header.customerId)
            : "";

        if (
          customerId &&
          !nextCustomers.some((c) => String(c.id) === customerId)
        ) {
          nextCustomers = [
            ...nextCustomers,
            {
              id: customerId,
              name: header.customerName && header.customerName !== "—"
                ? header.customerName
                : customerId,
            },
          ];
        }

        const seededLines = [];
        for (const [index, product] of (detail.products || []).entries()) {
          const lineId =
            product.customerAllocationLineId ||
            product.id ||
            `line-${index + 1}`;
          const productId = product.productId ? String(product.productId) : "";

          if (
            productId &&
            !nextProducts.some((p) => String(p.id) === productId)
          ) {
            nextProducts = [
              ...nextProducts,
              {
                id: productId,
                name: product.name || productId,
                partNumber: product.partNumber || "",
                uom: product.uom || "",
              },
            ];
          }

          let batches = [];
          let availableQty = 0;
          if (productId) {
            try {
              const allocationType =
                header.allocationType === "Service" ||
                header.orderType === "Service"
                  ? "Service"
                  : header.allocationType === "Sales" ||
                      header.orderType === "Sales"
                    ? header.allocationType || header.orderType
                    : "Sales";
              const batchResult =
                await inventoryManagementService.getAllocationBatches(
                  productId,
                  allocationType
                );
              if (cancelled) return;
              batches = enrichBatchesWithAllocationDetails(
                batchResult.batches || [],
                product.batches || []
              );
              availableQty = Number(batchResult.availableQty) || 0;
            } catch (err) {
              console.error("Failed to load batches for edit line:", err);
            }
          }

          const selectedBatchKeys = matchAllocatedBatchKeys(
            batches,
            product.batches || []
          );
          const batchQtyByKey = seedBatchQtyByKey(
            batches,
            product.batches || []
          );
          const iofQty = Number(product.requestedQty ?? product.iofQuantity) || 0;
          const seedAllocatedQty = Number(product.allocatedQty) || 0;
          const fullyAllocated =
            iofQty > 0 &&
            selectedBatchKeys.length > 0 &&
            seedAllocatedQty >= iofQty;
          const allocationDetails = (product.batches || [])
            .map((b) => ({
              customerAllocationLineDetailId: String(
                b.customerAllocationLineDetailId || ""
              ),
              allocatedQty: Number(b.allocatedQty) || 0,
              status: b.status || product.status || "",
            }))
            .filter((d) => d.customerAllocationLineDetailId);

          seededLines.push({
            id: String(lineId),
            customerAllocationLineId: product.customerAllocationLineId
              ? String(product.customerAllocationLineId)
              : "",
            allocationDetails,
            productId,
            productName: product.name || "",
            iofQty,
            consideredCost:
              product.consideredCostPerUnit != null &&
              product.consideredCostPerUnit !== ""
                ? String(product.consideredCostPerUnit)
                : "",
            selectedBatchKeys,
            batchQtyByKey,
            editingBatches: !fullyAllocated,
            batches,
            availableQty,
            loadingBatches: false,
            lineStatus: product.status || "",
            lineDeliveryDate: product.deliveryDate
              ? toInputDate(product.deliveryDate)
              : "",
            seedAllocatedQty,
            seedBatches: product.batches || [],
          });
        }

        const resolvedAllocationType =
          header.allocationType === "Service" ||
          header.orderType === "Service"
            ? "Service"
            : header.allocationType === "Sales" ||
                header.orderType === "Sales"
              ? header.allocationType || header.orderType
              : "Sales";
        setHeaderData({
          orderType: resolvedAllocationType,
          date: toInputDate(header.date) || todayIsoDate(),
          iofNumber:
            header.iofNumber && header.iofNumber !== "—"
              ? String(header.iofNumber)
              : "",
          customerId,
          projectName:
            header.projectName && header.projectName !== "—"
              ? String(header.projectName)
              : "",
          invoiceDate: toInputDate(header.invoiceDate) || todayIsoDate(),
          deliveryDate: toInputDate(header.deliveryDate) || todayIsoDate(),
          dispatchMethod: header.dispatchMethod || "FIFO",
        });
        setLines(seededLines.length > 0 ? seededLines : [emptyLine("line-1")]);
      }

      if (
        productsResult.status === "rejected" &&
        customersResult.status === "rejected"
      ) {
        setError(t("validation.failedToLoadDropdownMetadata"));
      }

      setProductsList(nextProducts);
      setCustomersList(nextCustomers);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [t, isEditMode, allocationId]);

  const handleProductScroll = (e) => {
    if (!productHasMore || loadingMoreProducts || loading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      loadProducts(productPage + 1, { append: true }).catch(() => {});
    }
  };

  const handleCustomerScroll = (e) => {
    if (!customerHasMore || loadingMoreCustomers || loading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      loadCustomers(customerPage + 1, { append: true }).catch(() => {});
    }
  };
  const handleHeaderChange = (key, value) => {
    setHeaderData((prev) => ({ ...prev, [key]: value }));
    setFormBanner("");
    if (formErrors[key]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleDispatchMethodChange = (method) => {
    setHeaderData((prev) => ({ ...prev, dispatchMethod: method }));
    setLines((prev) =>
      prev.map((l) => ({
        ...l,
        selectedBatchKeys: [],
        batchQtyByKey: {},
        editingBatches: true,
      }))
    );
  };

  const handleAddLine = () => {
    setLines((prev) => [...prev, emptyLine(`line-${Date.now()}`)]);
  };

  const handleRemoveLine = (id) => {
    if (lines.length === 1) {
      setFormBanner(t("inventory.atLeastOneProductLine"));
      return;
    }
    setFormBanner("");
    setLines((prev) => prev.filter((l) => l.id !== id));
    setQueuedUnlocks((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${id}::`)) delete next[key];
      });
      return next;
    });
  };

  const allocationStatus = allocationDetail?.header?.status || "";
  const canRequestRevokeByStatus =
    isEditMode && canActOnAllocationStatus(allocationStatus);

  const canMarkLineDelivered = (line) => {
    if (!isEditMode || !allocationDetail) return false;
    if (!canActOnAllocationStatus(allocationStatus)) return false;
    if (isDeliveredAllocationStatus(line.lineStatus, line.lineDeliveryDate)) {
      return false;
    }
    if (
      isPartiallyAllocatedHeaderStatus(allocationStatus) &&
      isUndeliverablePartialLineStatus(line.lineStatus)
    ) {
      return false;
    }
    const allocated =
      Number(line.allocatedQty) || Number(line.seedAllocatedQty) || 0;
    return allocated > 0 && Boolean(line.customerAllocationLineId || line.id);
  };

  const deliverBatchLabel = (line) => {
    const fromEntries = (line?.batchEntries || [])
      .map((b) => b.batchNumber)
      .filter((n) => n && n !== "—");
    if (fromEntries.length > 0) return fromEntries.join(", ");
    const fromSeed = (line?.seedBatches || [])
      .map((b) => b.batchNumber)
      .filter((n) => n && n !== "—");
    return fromSeed.length > 0 ? fromSeed.join(", ") : "—";
  };

  const openMarkAsDelivered = () => {
    setActionError("");
    setDeliverModalOpen(true);
  };

  const refreshAllocationAfterAction = async () => {
    const refreshed =
      await inventoryManagementService.getAllocationDetail(allocationId);
    setAllocationDetail(refreshed);
    const byLineId = new Map(
      (refreshed.products || []).map((p) => [
        String(p.customerAllocationLineId || p.id),
        p,
      ])
    );
    setLines((prev) =>
      prev.map((l) => {
        const key = String(l.customerAllocationLineId || l.id);
        const product = byLineId.get(key);
        if (!product) return l;
        return {
          ...l,
          lineStatus: product.status || l.lineStatus,
          lineDeliveryDate: product.deliveryDate
            ? toInputDate(product.deliveryDate)
            : "",
          seedAllocatedQty: Number(product.allocatedQty) || 0,
          seedBatches: product.batches || l.seedBatches || [],
          allocationDetails: (product.batches || [])
            .map((b) => ({
              customerAllocationLineDetailId: String(
                b.customerAllocationLineDetailId || ""
              ),
              allocatedQty: Number(b.allocatedQty) || 0,
              status: b.status || product.status || "",
            }))
            .filter((d) => d.customerAllocationLineDetailId),
          productName: product.name || l.productName,
        };
      })
    );
    return refreshed;
  };

  const handleMarkAsDeliveredConfirm = (deliveries) => {
    if (!allocationDetail || !deliveries?.length) return;
    setActionSubmitting(true);
    setActionError("");

    inventoryManagementService
      .markAllocationLinesDelivered({
        allocation: allocationDetail,
        deliveries,
      })
      .then(async () => {
        setDeliverModalOpen(false);
        setSuccessToast(t("formActions.markAsDeliveredSuccess"));
        setTimeout(() => setSuccessToast(""), 3000);
        try {
          await refreshAllocationAfterAction();
        } catch (refreshErr) {
          console.error(
            "Failed to refresh allocation after delivery:",
            refreshErr
          );
        }
        setActionSubmitting(false);
      })
      .catch((err) => {
        console.error("Failed to mark allocation lines as delivered:", err);
        setActionError(err?.message || t("validation.markAsDeliveredError"));
        setActionSubmitting(false);
      });
  };

  const loadBatchesForLine = useCallback(
    async (lineId, productId, allocationType) => {
      const type =
        allocationType ||
        headerData.orderType ||
        "Sales";
      setLines((prev) =>
        prev.map((l) =>
          l.id === lineId
            ? {
                ...l,
                loadingBatches: true,
                batches: [],
                availableQty: 0,
                selectedBatchKeys: [],
                batchQtyByKey: {},
              }
            : l
        )
      );
      try {
        const result = await inventoryManagementService.getAllocationBatches(
          productId,
          type
        );
        setLines((prev) =>
          prev.map((l) =>
            l.id === lineId && l.productId === productId
              ? {
                  ...l,
                  batches: result.batches || [],
                  availableQty: Number(result.availableQty) || 0,
                  loadingBatches: false,
                }
              : l
          )
        );
      } catch (err) {
        console.error("Failed to load available batches:", err);
        setLines((prev) =>
          prev.map((l) =>
            l.id === lineId
              ? { ...l, batches: [], availableQty: 0, loadingBatches: false }
              : l
          )
        );
      }
    },
    [headerData.orderType]
  );

  const handleAllocationTypeChange = (value) => {
    handleHeaderChange("orderType", value);
    setLines((prev) => {
      const next = prev.map((l) =>
        l.productId
          ? {
              ...l,
              selectedBatchKeys: [],
              batchQtyByKey: {},
              editingBatches: true,
              loadingBatches: true,
              batches: [],
              availableQty: 0,
            }
          : l
      );
      next.forEach((l) => {
        if (l.productId) {
          loadBatchesForLine(l.id, l.productId, value);
        }
      });
      return next;
    });
  };

  const handleLineProductChange = (id, productId) => {
    setLines((prev) =>
      prev.map((l) =>
        l.id === id
          ? {
              ...l,
              productId,
              iofQty: 0,
              consideredCost: "",
              selectedBatchKeys: [],
              batchQtyByKey: {},
              editingBatches: true,
              batches: [],
              availableQty: 0,
            }
          : l
      )
    );
    setQueuedUnlocks((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${id}::`)) delete next[key];
      });
      return next;
    });
    if (productId) {
      loadBatchesForLine(id, productId, headerData.orderType);
    }
  };

  const handleLineQtyChange = (id, iofQty) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const nextIof = Math.max(0, Number(iofQty) || 0);
        const nextQtyByKey =
          (l.selectedBatchKeys || []).length > 0
            ? autoFillQtyByKey(
                l.selectedBatchKeys,
                l.batches,
                nextIof,
                l.consideredCost
              )
            : {};
        return {
          ...l,
          iofQty: nextIof,
          batchQtyByKey: nextQtyByKey,
          editingBatches: true,
        };
      })
    );
  };

  const handleLineCostChange = (id, consideredCost) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const nextSelected = l.selectedBatchKeys.filter((key) => {
          const batch = l.batches.find((b) => b.key === key);
          if (!batch) return false;
          const queueKey = unlockQueueKey(l.id, batch.key);
          const unlockStatus = queuedUnlocks[queueKey]
            ? "QUEUED"
            : resolveUnlockStatus(batch, unlockStatusByDetailId);
          return (
            batchEligibility(batch, consideredCost, unlockStatus) ===
            "eligible"
          );
        });
        const nextQtyByKey = autoFillQtyByKey(
          nextSelected,
          l.batches,
          l.iofQty,
          consideredCost
        );
        return {
          ...l,
          consideredCost,
          editingBatches: true,
          selectedBatchKeys: nextSelected,
          batchQtyByKey: nextQtyByKey,
        };
      })
    );
  };

  const handleToggleBatch = (lineId, batchKey, eligible) => {
    if (!eligible) return;
    setLines((prev) =>
      prev.map((l) => {
        if (l.id !== lineId) return l;

        let nextSelected = [...(l.selectedBatchKeys || [])];
        const nextQtyByKey = { ...(l.batchQtyByKey || {}) };
        const iofQty = Number(l.iofQty) || 0;

        if (nextSelected.includes(batchKey)) {
          nextSelected = nextSelected.filter((k) => k !== batchKey);
          delete nextQtyByKey[batchKey];
        } else {
          nextSelected.push(batchKey);
          nextQtyByKey[batchKey] = suggestQtyForBatch(
            nextSelected,
            l.batches,
            iofQty,
            l.consideredCost,
            nextQtyByKey,
            batchKey
          );
        }

        const { allocatedQty } = buildAllocationFromQtyByKey(
          nextSelected,
          l.batches,
          l.consideredCost,
          nextQtyByKey
        );
        const fullyAllocated = iofQty > 0 && allocatedQty >= iofQty;

        return {
          ...l,
          selectedBatchKeys: nextSelected,
          batchQtyByKey: nextQtyByKey,
          editingBatches: !fullyAllocated,
        };
      })
    );
  };

  const handleBatchAllocatedQtyChange = (lineId, batchKey, value) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.id !== lineId) return l;
        const batch = (l.batches || []).find((b) => b.key === batchKey);
        const available = Math.max(0, Number(batch?.availableQty) || 0);
        const parsed =
          value === "" || value == null ? "" : Math.max(0, Number(value) || 0);
        const capped =
          parsed === ""
            ? ""
            : Math.min(parsed, available > 0 ? available : parsed);
        return {
          ...l,
          batchQtyByKey: {
            ...(l.batchQtyByKey || {}),
            [batchKey]: capped,
          },
          editingBatches: true,
        };
      })
    );
  };

  const computedLines = useMemo(() => {
    const method = headerData.dispatchMethod || "FIFO";

    return lines.map((line) => {
      const product = productsList.find((p) => p.id === line.productId);
      // Display order follows dispatch method; allocation follows tick order + edits.
      const sortedBatches = sortBatches(line.batches, method);

      const batchesWithEligibility = sortedBatches.map((b) => {
        const queueKey = unlockQueueKey(line.id, b.key);
        const queued = queuedUnlocks[queueKey];
        const unlockStatus = queued
          ? "QUEUED"
          : resolveUnlockStatus(b, unlockStatusByDetailId);
        return {
          ...b,
          unlockStatus,
          eligibility: batchEligibility(b, line.consideredCost, unlockStatus),
        };
      });

      const costEntered =
        line.consideredCost !== "" &&
        line.consideredCost != null &&
        !Number.isNaN(Number(line.consideredCost));

      const iofQty = Number(line.iofQty) || 0;
      const { allocationByKey, batchEntries, allocatedQty } =
        buildAllocationFromQtyByKey(
          line.selectedBatchKeys,
          batchesWithEligibility,
          line.consideredCost,
          line.batchQtyByKey
        );
      const shortfall = Math.max(0, iofQty - allocatedQty);

      const priceEligibleQty = batchesWithEligibility
        .filter((b) => b.eligibility === "eligible")
        .reduce((sum, b) => sum + (Number(b.availableQty) || 0), 0);
      const totalBatchQty = batchesWithEligibility.reduce(
        (sum, b) => sum + (Number(b.availableQty) || 0),
        0
      );
      const hasBatches = batchesWithEligibility.length > 0;
      const allBlocked =
        costEntered &&
        hasBatches &&
        batchesWithEligibility.every((b) => b.eligibility === "blocked");
      const blockedQty = batchesWithEligibility
        .filter((b) => b.eligibility === "blocked")
        .reduce((sum, b) => sum + (Number(b.availableQty) || 0), 0);

      let lineEligibility = null;
      if (line.productId && iofQty > 0 && costEntered) {
        if (allBlocked || (priceEligibleQty <= 0 && blockedQty > 0)) {
          lineEligibility = "blocked";
        } else if (shortfall > 0 && line.selectedBatchKeys.length > 0) {
          // Selected batches cannot cover IOF (e.g. IOF 300, selected 200)
          lineEligibility = "shortfall";
        } else if (priceEligibleQty < iofQty) {
          lineEligibility = "shortfall";
        } else {
          lineEligibility = "eligible";
        }
      }

      // Collapse only when fully allocated (no shortfall)
      const isCollapsed =
        line.productId &&
        !line.editingBatches &&
        allocatedQty > 0 &&
        shortfall === 0;

      return {
        ...line,
        productName: product?.name || line.productName || "",
        partNumber: product?.partNumber || "—",
        uom: product?.uom || "—",
        metricId: product?.metricId || "",
        sortedBatches: batchesWithEligibility,
        allocationByKey,
        batchEntries,
        allocatedQty,
        shortfall,
        priceEligibleQty,
        totalBatchQty,
        blockedQty,
        lineEligibility,
        isCollapsed,
      };
    });
  }, [lines, productsList, headerData.dispatchMethod, unlockStatusByDetailId, queuedUnlocks]);

  const deliverableProducts = useMemo(() => {
    if (!isEditMode) return [];
    return computedLines
      .filter((line) => canMarkLineDelivered(line))
      .map((line) => {
        const detailProduct = (allocationDetail?.products || []).find(
          (p) =>
            String(p.customerAllocationLineId || p.id) ===
            String(line.customerAllocationLineId || line.id)
        );
        return {
          id: String(line.customerAllocationLineId || line.id),
          name: line.productName,
          batchNumber: deliverBatchLabel(line),
          allocatedQty: line.allocatedQty ?? line.seedAllocatedQty,
          invoiceNumber:
            (detailProduct?.invoiceNumber &&
            detailProduct.invoiceNumber !== "—"
              ? String(detailProduct.invoiceNumber)
              : "") ||
            (allocationDetail?.header?.invoiceNumber &&
            allocationDetail.header.invoiceNumber !== "—"
              ? String(allocationDetail.header.invoiceNumber)
              : ""),
          deliveryNotes: detailProduct?.deliveryNotes || "",
        };
      });
  }, [isEditMode, computedLines, allocationDetail, allocationStatus]);

  const revocableProducts = useMemo(() => {
    if (!isEditMode || !canRequestRevokeByStatus) return [];
    return computedLines
      .filter((line) => {
        // Only non-delivered products appear in the revoke modal
        if (isDeliveredAllocationStatus(line.lineStatus, line.lineDeliveryDate)) {
          return false;
        }
        if (!line.productId) return false;
        const details = (line.allocationDetails || []).filter((d) => {
          if (!d.customerAllocationLineDetailId) return false;
          if (
            pendingRevokeDetailIds.has(
              String(d.customerAllocationLineDetailId)
            )
          ) {
            return false;
          }
          if (isDeliveredAllocationStatus(d.status)) return false;
          if (isPendingApprovalStatus(d.status)) return false;
          return true;
        });
        return details.length > 0;
      })
      .map((line) => ({
        id: String(line.customerAllocationLineId || line.id),
        name: line.productName,
        allocatedQty: line.allocatedQty ?? line.seedAllocatedQty,
        allocationDetails: (line.allocationDetails || []).filter((d) => {
          if (!d.customerAllocationLineDetailId) return false;
          if (
            pendingRevokeDetailIds.has(
              String(d.customerAllocationLineDetailId)
            )
          ) {
            return false;
          }
          if (isDeliveredAllocationStatus(d.status)) return false;
          if (isPendingApprovalStatus(d.status)) return false;
          return true;
        }),
      }));
  }, [
    isEditMode,
    canRequestRevokeByStatus,
    computedLines,
    pendingRevokeDetailIds,
  ]);

  const canRequestRevoke = canRequestRevokeByStatus && revocableProducts.length > 0;

  const handleRevokeSubmit = ({ reason, lineIds = [] } = {}) => {
    if (!lineIds.length) return;
    setActionSubmitting(true);
    setActionError("");

    const selectedLines = revocableProducts.filter((p) =>
      lineIds.map(String).includes(String(p.id))
    );

    const detailPayloads = selectedLines.flatMap((line) =>
      (line.allocationDetails || []).map((detail) => ({
        customerAllocationDetailId: Number(
          detail.customerAllocationLineDetailId
        ),
        revokeQuantity: Number(detail.allocatedQty) || 0,
      }))
    );

    if (detailPayloads.length === 0) {
      setActionError(t("inventory.noRevocableProducts"));
      setActionSubmitting(false);
      return;
    }

    Promise.all(
      detailPayloads.map((detail) =>
        inventoryManagementService.createRevokeUnlockRequest({
          customerAllocationDetailId: detail.customerAllocationDetailId,
          actionType: "REVOKE",
          revokeQuantity: detail.revokeQuantity,
          reason,
        })
      )
    )
      .then(async (results) => {
        const submittedIds = detailPayloads.map((d) =>
          String(d.customerAllocationDetailId)
        );
        (results || []).forEach((res) => {
          const id = res?.customerAllocationDetailId;
          if (id != null) submittedIds.push(String(id));
        });
        setPendingRevokeDetailIds((prev) => {
          const next = new Set(prev);
          submittedIds.forEach((id) => next.add(id));
          return next;
        });
        setRevokeModalOpen(false);
        setSuccessToast(t("formActions.revokeSubmittedSuccess"));
        setTimeout(() => setSuccessToast(""), 3000);
        try {
          await refreshAllocationAfterAction();
        } catch (refreshErr) {
          console.error(
            "Failed to refresh allocation after revoke request:",
            refreshErr
          );
        }
        setActionSubmitting(false);
      })
      .catch((err) => {
        console.error("Failed to submit revoke request:", err);
        setActionError(err?.message || t("validation.revokeSubmitError"));
        setActionSubmitting(false);
      });
  };

  const showLineHeaderActions =
    isEditMode && (deliverableProducts.length > 0 || canRequestRevoke);

  const handleCancel = () => {
    navigate("/inventory-management/customer-allocations");
  };

  /**
   * After create/update: POST UNLOCK for each queued item when a real
   * customerAllocationLineDetailId exists (from details[]). Queued unlock
   * still posts even if priceOverrides made the line Eligible.
   */
  const flushQueuedUnlockRequests = async (
    savedAllocationId,
    saveResponse,
    queueSnapshot = queuedUnlocks
  ) => {
    const queued = Object.values(queueSnapshot || {});
    if (!queued.length || !savedAllocationId) {
      return { submitted: false, skipped: 0 };
    }

    const rawLines = Array.isArray(saveResponse?.lines)
      ? saveResponse.lines
      : Array.isArray(saveResponse?.data?.lines)
        ? saveResponse.data.lines
        : [];

    console.info("[unlock-flush] savedId=", savedAllocationId);
    console.info("[unlock-flush] queued=", queued);
    console.info("[unlock-flush] create lines=", rawLines);

    let mappedDetail = null;
    const needsDetailRefresh =
      rawLines.length === 0 ||
      rawLines.some(
        (l) => !Array.isArray(l.details) || l.details.length === 0
      );
    if (needsDetailRefresh) {
      try {
        mappedDetail =
          await inventoryManagementService.getAllocationDetail(savedAllocationId);
      } catch (getErr) {
        console.warn("[unlock-flush] GET detail failed", getErr);
      }
    }

    const requests = [];
    let skipped = 0;

    for (const item of queued) {
      let resolved = resolveQueuedUnlockAction(
        item,
        saveResponse,
        mappedDetail
      );

      if (
        (resolved.action === "missing_detail" ||
          resolved.action === "missing_line") &&
        mappedDetail
      ) {
        const fromGet = resolveQueuedUnlockAction(item, null, mappedDetail);
        if (fromGet.action === "unlock") {
          resolved = fromGet;
        }
      }

      console.info(
        "[unlock-flush] resolved=",
        resolved,
        "for product",
        item.productId
      );

      if (resolved.action === "skip") {
        skipped += 1;
        continue;
      }

      if (
        resolved.action === "missing_line" ||
        resolved.action === "missing_detail" ||
        resolved.detailId == null
      ) {
        throw new Error(t("inventory.unlockDetailMissingAfterSave"));
      }

      requests.push(
        inventoryManagementService.createRevokeUnlockRequest({
          customerAllocationDetailId: Number(resolved.detailId),
          actionType: "UNLOCK",
          unlockedPrice: Number(item.unlockedPrice) || 0,
          reason: item.reason,
        })
      );
    }

    if (requests.length > 0) {
      console.info(
        "[unlock-flush] POSTing",
        requests.length,
        "unlock request(s)"
      );
      await Promise.all(requests);
    }

    return { submitted: requests.length > 0, skipped };
  };

  const handleSave = (e) => {
    e.preventDefault();
    const errors = {};

    if (!headerData.orderType) {
      errors.orderType = t("validation.fieldRequired", {
        field: t("inventory.salesAndService"),
      });
    }
    if (!headerData.iofNumber?.trim()) {
      errors.iofNumber = t("validation.fieldRequired", {
        field: t("inventory.iofNumber"),
      });
    }
    if (!headerData.customerId) {
      errors.customerId = t("validation.fieldRequired", {
        field: t("inventory.customerName"),
      });
    }
    if (!headerData.invoiceDate) {
      errors.invoiceDate = t("validation.fieldRequired", {
        field: t("dateTime.invoiceDate"),
      });
    }
    if (!headerData.deliveryDate) {
      errors.deliveryDate = t("validation.fieldRequired", {
        field: t("dateTime.finalDeliveryDate"),
      });
    }

    const lineErrors = [];
    computedLines.forEach((l, index) => {
      if (!l.productId) {
        lineErrors[index] = t("validation.fieldRequired", {
          field: t("inventory.product"),
        });
      } else if ((Number(l.iofQty) || 0) <= 0) {
        lineErrors[index] = t("validation.qtyMustBeGreaterThanZero", {
          field: t("inventory.iofQty"),
        });
      } else if (
        l.consideredCost === "" ||
        l.consideredCost == null ||
        Number.isNaN(Number(l.consideredCost))
      ) {
        lineErrors[index] = t("validation.fieldRequired", {
          field: t("inventory.consideredCostPerUnit"),
        });
      }
    });

    if (lineErrors.some(Boolean)) {
      errors.lines = lineErrors;
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setFormBanner(t("validation.validationErrors"));
      return;
    }

    setFormBanner("");
    setFormErrors({});
    setSubmitting(true);

    // Snapshot queue now — unlock POST runs after create using this create response.
    const unlockQueueSnapshot = { ...queuedUnlocks };
    const hasQueuedUnlocks = Object.keys(unlockQueueSnapshot).length > 0;
    console.info(
      "[unlock-save] hasQueuedUnlocks=",
      hasQueuedUnlocks,
      unlockQueueSnapshot
    );

    const buildPriceOverrides = (line) => {
      // Queued unlock needs a real detail id. Send the blocked batch as
      // priceOverrides so details[] is created (may return Eligible).
      // Flush still POSTs UNLOCK using that detail id.
      const queuedForLine = Object.values(unlockQueueSnapshot).filter(
        (q) => String(q.lineId) === String(line.id)
      );
      if (queuedForLine.length > 0) {
        const iofQty = Number(line.iofQty) || 0;
        let remaining = iofQty > 0 ? iofQty : 0;
        const overrides = [];
        for (const q of queuedForLine) {
          if (remaining <= 0) break;
          const batch =
            (line.sortedBatches || []).find((b) => b.key === q.batchKey) ||
            (line.batches || []).find((b) => b.key === q.batchKey);
          const avail = Math.max(
            0,
            Number(batch?.availableQty) || remaining
          );
          const take = Math.min(remaining, avail);
          if (take <= 0) continue;
          overrides.push({
            warehouseId: Number(q.warehouseId),
            batchNumber:
              q.batchNumber === "—" ? "" : String(q.batchNumber || ""),
            purchasePricePerUnit: Number(q.purchasePricePerUnit) || 0,
            allocatedQuantity: take,
            overrideReason: "Pending price unlock",
          });
          remaining -= take;
        }
        return overrides;
      }

      // Prefer what the UI allocated in tick order (batchEntries).
      const fromSelection = (line.batchEntries || [])
        .filter((b) => (Number(b.qty) || 0) > 0)
        .map((b) => ({
          warehouseId: Number(b.warehouseId),
          batchNumber: b.batchNumber === "—" ? "" : String(b.batchNumber || ""),
          purchasePricePerUnit: Number(b.purchasePricePerUnit) || 0,
          allocatedQuantity: Number(b.qty) || 0,
          overrideReason: "test",
        }));

      if (fromSelection.length > 0) return fromSelection;

      // Nothing ticked: only then fall back to dispatch-method order.
      if ((line.selectedBatchKeys || []).length > 0) return [];

      const iofQty = Number(line.iofQty) || 0;
      if (iofQty <= 0 || (Number(line.priceEligibleQty) || 0) <= 0) return [];

      const method = headerData.dispatchMethod || "FIFO";
      let remaining = iofQty;
      const overrides = [];
      const eligibleSorted = sortBatches(
        (line.sortedBatches || []).filter((b) => b.eligibility === "eligible"),
        method
      );
      for (const b of eligibleSorted) {
        if (remaining <= 0) break;
        const take = Math.min(remaining, Number(b.availableQty) || 0);
        if (take <= 0) continue;
        overrides.push({
          warehouseId: Number(b.warehouseId),
          batchNumber: b.batchNumber === "—" ? "" : String(b.batchNumber || ""),
          purchasePricePerUnit: Number(b.purchasePricePerUnit) || 0,
          allocatedQuantity: take,
          overrideReason: "test",
        });
        remaining -= take;
      }
      return overrides;
    };

    const allocationType = headerData.orderType || "Sales";
    const payload = {
      allocationType,
      orderType: allocationType,
      orderDate: headerData.date,
      iofNumber: headerData.iofNumber || null,
      customerId: headerData.customerId,
      projectName: headerData.projectName || null,
      customerInvoiceDate: headerData.invoiceDate,
      deliveryDate: headerData.deliveryDate,
      dispatchMethod: headerData.dispatchMethod,
      lines: computedLines.map((l) => {
        const lineBody = {
          productId: l.productId,
          iofQuantity: Number(l.iofQty) || 0,
          consideredCostPerUnit: Number(l.consideredCost) || 0,
          priceOverrides: buildPriceOverrides(l),
        };
        if (l.customerAllocationLineId) {
          lineBody.customerAllocationLineId = l.customerAllocationLineId;
        }
        return lineBody;
      }),
    };

    const savePromise = isEditMode
      ? inventoryManagementService.updateCustomerAllocation(allocationId, payload)
      : inventoryManagementService.createAllocation(payload);

    savePromise
      .then(async (res) => {
        const savedId = res.allocationId || allocationId;
        let unlockSubmitted = false;

        if (hasQueuedUnlocks && savedId) {
          try {
            const unlockResult = await flushQueuedUnlockRequests(
              savedId,
              res,
              unlockQueueSnapshot
            );
            unlockSubmitted = Boolean(unlockResult?.submitted);
            setQueuedUnlocks({});
          } catch (unlockErr) {
            console.error(
              "Allocation saved but unlock request(s) failed:",
              unlockErr
            );
            setSubmitting(false);
            setFormBanner(
              unlockErr?.message || t("inventory.unlockSubmitError")
            );
            setSuccessToast(
              isEditMode
                ? t("inventory.allocationUpdatedSuccess")
                : t("inventory.allocationSavedSuccess")
            );
            setTimeout(() => {
              setSuccessToast("");
              navigate(
                `/inventory-management/allocate-to-customer/edit/${savedId}`
              );
            }, 1800);
            return;
          }
        }

        setSuccessToast(
          isEditMode
            ? t("inventory.allocationUpdatedSuccess")
            : unlockSubmitted
              ? t("inventory.allocationSavedWithUnlockSuccess")
              : hasQueuedUnlocks
                ? t("inventory.allocationSavedUnlockSkipped")
                : t("inventory.allocationSavedSuccess")
        );
        setSubmitting(false);
        setTimeout(() => {
          setSuccessToast("");
          navigate(
            `/inventory-management/allocation-detail/${savedId || "1"}`
          );
        }, 1500);
      })
      .catch((err) => {
        console.error("Failed to save customer allocation payload:", err);
        setFormBanner(
          err?.message ||
            (isEditMode
              ? t("validation.allocationUpdateError")
              : t("validation.allocationSaveError"))
        );
        setSubmitting(false);
      });
  };

  const eligibilityLabel = (status) => {
    if (status === "awaiting") return t("inventory.awaitingConsideredCost");
    if (status === "blocked") return t("inventory.blockedByPrice");
    if (status === "eligible") return t("inventory.eligible");
    if (status === "shortfall") return t("inventory.shortfall");
    return "—";
  };

  const openUnlockRequest = (line, batch) => {
    setUnlockError("");
    setActionError("");
    setUnlockTarget({ line, batch });
    setUnlockModalOpen(true);
  };

  const handleUnlockSubmit = ({ reason } = {}) => {
    if (!unlockTarget?.batch || !unlockTarget?.line) return;

    const batch = unlockTarget.batch;
    const line = unlockTarget.line;
    const detailId = batch.customerAllocationLineDetailId
      ? Number(batch.customerAllocationLineDetailId)
      : null;
    const lineAlreadyBlocked = isBlockedByPriceStatus(line.lineStatus);

    // Edit + detail exists + line already Blocked_by_price: submit unlock now.
    if (
      isEditMode &&
      detailId != null &&
      Number.isFinite(detailId) &&
      lineAlreadyBlocked
    ) {
      setActionSubmitting(true);
      setUnlockError("");
      inventoryManagementService
        .createRevokeUnlockRequest({
          customerAllocationDetailId: detailId,
          actionType: "UNLOCK",
          unlockedPrice: Number(line.consideredCost) || 0,
          reason,
        })
        .then(() => {
          setUnlockStatusByDetailId((prev) => ({
            ...prev,
            [String(detailId)]: "PENDING",
          }));
          setUnlockModalOpen(false);
          setUnlockTarget(null);
          setSuccessToast(t("inventory.unlockRequestSubmittedSuccess"));
          setTimeout(() => setSuccessToast(""), 3000);
          setActionSubmitting(false);
        })
        .catch((err) => {
          console.error("Failed to submit unlock request:", err);
          setUnlockError(err?.message || t("inventory.unlockSubmitError"));
          setActionSubmitting(false);
        });
      return;
    }

    // Save sends queued blocked batches as priceOverrides (creates detail id),
    // then unlock POST uses that detail id even if line status is Eligible.
    const key = unlockQueueKey(line.id, batch.key);
    setQueuedUnlocks((prev) => ({
      ...prev,
      [key]: {
        lineId: line.id,
        batchKey: batch.key,
        productId: line.productId,
        warehouseId: batch.warehouseId,
        batchNumber: batch.batchNumber,
        purchasePricePerUnit: batch.purchasePricePerUnit,
        unlockedPrice: Number(line.consideredCost) || 0,
        reason,
      },
    }));
    setUnlockModalOpen(false);
    setUnlockTarget(null);
    setUnlockError("");
  };

  const fieldLabelClass =
    "text-[11px] font-bold uppercase tracking-wider per-file-hint";
  const fieldInputClass =
    "w-full h-10 px-3 rounded-xl border border-(--color-text-border) bg-white text-xs font-semibold focus:border-accent focus:outline-hidden";

  return (
    <AppLayout hideBreadcrumb className="space-y-6">
      {successToast ? <InventoryActionToast toast={successToast} /> : null}

      <MarkAsDeliveredModal
        open={deliverModalOpen}
        onOpenChange={setDeliverModalOpen}
        customerId={
          headerData?.customerId || allocationDetail?.header?.customerId
        }
        products={deliverableProducts}
        submitting={actionSubmitting}
        onConfirm={handleMarkAsDeliveredConfirm}
      />

      <RequestRevokeModal
        open={revokeModalOpen}
        onOpenChange={setRevokeModalOpen}
        referenceCode={allocationDetail?.header?.reference}
        products={revocableProducts}
        submitting={actionSubmitting}
        submitError={actionError}
        onSubmit={handleRevokeSubmit}
      />

      <RequestUnlockModal
        open={unlockModalOpen}
        onOpenChange={(open) => {
          setUnlockModalOpen(open);
          if (!open) {
            setUnlockTarget(null);
            setUnlockError("");
          }
        }}
        batchLabel={unlockTarget?.batch?.batchNumber || ""}
        purchasePrice={
          unlockTarget?.batch
            ? formatCurrency(unlockTarget.batch.purchasePricePerUnit)
            : "—"
        }
        consideredCost={
          unlockTarget?.line?.consideredCost != null &&
          unlockTarget?.line?.consideredCost !== ""
            ? formatCurrency(unlockTarget.line.consideredCost)
            : "—"
        }
        submitting={actionSubmitting}
        submitError={unlockError}
        onSubmit={handleUnlockSubmit}
      />

      <PageTitleRow
        back={
          <PageBackLink
            to="/inventory-management/customer-allocations"
            label={t("formActions.back")}
          />
        }
      >
        <h1 className="text-xl sm:text-2xl font-extrabold text-foreground tracking-tight">
          {isEditMode
            ? t("inventory.editAllocateTitle")
            : t("inventory.allocateTitle")}
        </h1>
      </PageTitleRow>

      {loading ? (
        <ContentCard bodyClassName="p-6 h-[450px] animate-pulse bg-gray-50/20" />
      ) : error ? (
        <div className="p-8 text-center text-error font-semibold text-sm border bg-white rounded-2xl">
          ⚠️ {error}
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          {formBanner ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700">
              {formBanner}
            </div>
          ) : null}
          {actionError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700">
              {actionError}
            </div>
          ) : null}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-sans">
            <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4 opacity-50 cursor-not-allowed flex items-start gap-3">
              <input
                type="radio"
                disabled
                checked={false}
                className="mt-1 h-4 w-4 border-gray-300 text-gray-400 focus:ring-0 cursor-not-allowed"
              />
              <div className="space-y-1">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                  {t("formActions.importFromIof")}
                </h4>
                <p className="text-[11px] text-gray-400 font-semibold leading-relaxed">
                  {t("formActions.importFromIofHint")}
                </p>
              </div>
            </div>

            <div className="rounded-2xl border-2 border-accent bg-white p-4 shadow-xs flex items-start gap-3">
              <input
                type="radio"
                checked
                readOnly
                className="mt-1 h-4 w-4 border-gray-300 text-accent focus:ring-0 cursor-pointer"
              />
              <div className="space-y-1">
                <h4 className="text-xs font-bold text-foreground uppercase tracking-wide">
                  {t("inventory.enterManually")}
                </h4>
                <p className="text-[11px] per-file-hint font-semibold leading-relaxed">
                  {t("inventory.enterManuallyHint")}
                </p>
              </div>
            </div>
          </div>

          <ContentCard title={t("inventory.orderHeader")} bodyClassName="p-3 sm:p-4 font-sans">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-3">
              <div className="space-y-1">
                <label className={fieldLabelClass}>
                  {t("inventory.salesAndService")}{" "}
                  <span className="text-rose-500">*</span>
                </label>
                <Select
                  value={headerData.orderType}
                  onValueChange={handleAllocationTypeChange}
                >
                  <SelectTrigger
                    className={cn(
                      "h-10 rounded-xl border bg-white text-xs font-semibold focus:ring-accent w-full",
                      formErrors.orderType
                        ? "border-rose-500"
                        : "border-(--color-text-border)"
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sales">{t("inventory.sales")}</SelectItem>
                    <SelectItem value="Service">{t("inventory.service")}</SelectItem>
                  </SelectContent>
                </Select>
                {formErrors.orderType && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">
                    {formErrors.orderType}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className={fieldLabelClass}>
                  {t("dateTime.date")} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={headerData.date}
                  onChange={(e) => handleHeaderChange("date", e.target.value)}
                  className={fieldInputClass}
                />
              </div>

              <div className="space-y-1">
                <label className={fieldLabelClass}>
                  {t("inventory.iofNo")} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={headerData.iofNumber}
                  onChange={(e) => handleHeaderChange("iofNumber", e.target.value)}
                  placeholder={t("inventory.iofNumber")}
                  className={cn(
                    fieldInputClass,
                    formErrors.iofNumber && "border-rose-500"
                  )}
                />
                {formErrors.iofNumber && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">
                    {formErrors.iofNumber}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className={fieldLabelClass}>
                  {t("inventory.customerName")} <span className="text-rose-500">*</span>
                </label>
                <Select
                  value={headerData.customerId}
                  onValueChange={(val) => handleHeaderChange("customerId", val)}
                >
                  <SelectTrigger
                    className={cn(
                      "h-10 rounded-xl border bg-white text-xs font-semibold focus:ring-accent w-full",
                      formErrors.customerId
                        ? "border-rose-500"
                        : "border-(--color-text-border)"
                    )}
                  >
                    <SelectValue placeholder={t("inventory.selectCustomer")} />
                  </SelectTrigger>
                  <SelectContent
                    viewportClassName="h-auto max-h-60 overflow-y-auto"
                    onViewportScroll={handleCustomerScroll}
                  >
                    {customersList.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                    {loadingMoreCustomers ? (
                      <div className="px-3 py-2 text-[11px] font-semibold per-file-hint">
                        {t("common.loading")}
                      </div>
                    ) : null}
                  </SelectContent>
                </Select>
                {formErrors.customerId && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">
                    {formErrors.customerId}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className={fieldLabelClass}>
                  {t("inventory.projectName")}
                </label>
                <input
                  type="text"
                  value={headerData.projectName}
                  onChange={(e) => handleHeaderChange("projectName", e.target.value)}
                  className={fieldInputClass}
                />
              </div>

              <div className="space-y-1">
                <label className={fieldLabelClass}>
                  {t("dateTime.invoiceDate")} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={headerData.invoiceDate}
                  onChange={(e) => handleHeaderChange("invoiceDate", e.target.value)}
                  className={cn(
                    fieldInputClass,
                    formErrors.invoiceDate && "border-rose-500"
                  )}
                />
                {formErrors.invoiceDate && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">
                    {formErrors.invoiceDate}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className={fieldLabelClass}>
                  {t("dateTime.finalDeliveryDate")} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={headerData.deliveryDate}
                  onChange={(e) => handleHeaderChange("deliveryDate", e.target.value)}
                  className={cn(
                    fieldInputClass,
                    formErrors.deliveryDate && "border-rose-500"
                  )}
                />
                {formErrors.deliveryDate && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">
                    {formErrors.deliveryDate}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className={fieldLabelClass}>
                  {t("inventory.dispatchMethod")}
                </label>
                <Select
                  value={headerData.dispatchMethod}
                  onValueChange={handleDispatchMethodChange}
                >
                  <SelectTrigger className="h-10 rounded-xl border border-(--color-text-border) bg-white text-xs font-semibold focus:ring-accent w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FIFO">{t("inventory.fifoLabel")}</SelectItem>
                    <SelectItem value="FEFO">{t("inventory.fefoLabel")}</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-[10px] text-gray-400 block mt-1">
                  {t("inventory.fifoFefoOrderHint")}
                </span>
              </div>
            </div>
          </ContentCard>

          <ContentCard
            title={t("inventory.lineDetails")}
            bodyClassName="p-0 font-sans"
            action={
              showLineHeaderActions ? (
                <div className="flex flex-wrap items-center gap-2">
                  {deliverableProducts.length > 0 ? (
                    <button
                      type="button"
                      disabled={actionSubmitting}
                      onClick={openMarkAsDelivered}
                      className="h-8 px-3 rounded-lg border border-emerald-200 bg-white text-[11px] font-bold text-emerald-700 hover:bg-emerald-50 transition cursor-pointer disabled:opacity-50"
                    >
                      {t("formActions.markAsDelivered")}
                    </button>
                  ) : null}
                  {canRequestRevoke ? (
                    <button
                      type="button"
                      disabled={actionSubmitting}
                      onClick={() => {
                        setActionError("");
                        setRevokeModalOpen(true);
                      }}
                      className="h-8 px-3 rounded-lg border border-rose-200 bg-white text-[11px] font-bold text-rose-600 hover:bg-rose-50 transition cursor-pointer disabled:opacity-50"
                    >
                      {t("formActions.requestRevoke")}
                    </button>
                  ) : null}
                </div>
              ) : null
            }
          >
            {/* <p className="px-4 sm:px-6 pt-3 pb-1 text-[11px] font-medium per-file-hint">
              {t("inventory.lineDetailsHint")}
            </p> */}
            <div className="overflow-x-auto">
              <ClientTable className="w-full min-w-[960px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-(--color-text-border)/60 bg-gray-50/40">
                    <th className="px-4 py-3 text-[10px] font-extrabold uppercase tracking-wider text-gray-400 w-10">
                      #
                    </th>
                    <th className="px-3 py-3 text-[10px] font-extrabold uppercase tracking-wider text-gray-400 min-w-[200px]">
                      {t("inventory.product")}
                    </th>
                    <th className="px-3 py-3 text-[10px] font-extrabold uppercase tracking-wider text-gray-400 w-24">
                      {t("inventory.iofQty")}
                    </th>
                    <th className="px-3 py-3 text-[10px] font-extrabold uppercase tracking-wider text-gray-400 w-16">
                      {t("productMetrics.uom")}
                    </th>
                    <th className="px-3 py-3 text-[10px] font-extrabold uppercase tracking-wider text-gray-400 min-w-[140px]">
                      {t("inventory.consideredCostPerUnit")}
                    </th>
                    <th className="px-3 py-3 text-[10px] font-extrabold uppercase tracking-wider text-gray-400 w-28">
                      {t("status.available")}
                    </th>
                    <th className="px-3 py-3 text-[10px] font-extrabold uppercase tracking-wider text-gray-400 min-w-[120px]">
                      {t("inventory.partNumber")}
                    </th>
                    <th className="px-4 py-3 text-[10px] font-extrabold uppercase tracking-wider text-gray-400 w-20 text-right">
                      {" "}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {computedLines.map((line, index) => (
                    <React.Fragment key={line.id}>
                      <tr className="border-b border-(--color-text-border)/40 align-top">
                        <td className="px-4 py-4 text-xs font-extrabold text-foreground font-mono">
                          {index + 1}
                        </td>
                        <td className="px-3 py-3">
                          <Select
                            value={line.productId}
                            onValueChange={(val) =>
                              handleLineProductChange(line.id, val)
                            }
                          >
                            <SelectTrigger
                              className={cn(
                                "h-10 rounded-lg border bg-white text-xs font-semibold w-full min-w-[180px]",
                                formErrors.lines?.[index]
                                  ? "border-rose-500"
                                  : "border-(--color-text-border)"
                              )}
                            >
                              <SelectValue
                                placeholder={t("inventory.selectProduct")}
                              />
                            </SelectTrigger>
                            <SelectContent
                              viewportClassName="h-auto max-h-60 overflow-y-auto"
                              onViewportScroll={handleProductScroll}
                            >
                              {productsList.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name}
                                </SelectItem>
                              ))}
                              {loadingMoreProducts ? (
                                <div className="px-3 py-2 text-[11px] font-semibold per-file-hint">
                                  {t("common.loading")}
                                </div>
                              ) : null}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-3">
                          <input
                            type="number"
                            min="0"
                            value={line.iofQty || ""}
                            onChange={(e) =>
                              handleLineQtyChange(line.id, e.target.value)
                            }
                            placeholder="0"
                            className={cn(
                              "w-20 h-10 px-2 rounded-lg border bg-white text-xs font-semibold focus:border-accent focus:outline-hidden",
                              formErrors.lines?.[index]
                                ? "border-rose-500"
                                : "border-(--color-text-border)"
                            )}
                          />
                        </td>
                        <td className="px-3 py-3 text-xs font-semibold text-foreground pt-5">
                          {line.productId ? line.uom : "—"}
                        </td>
                        <td className="px-3 py-3">
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-semibold">
                              ₹
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.consideredCost}
                              onChange={(e) =>
                                handleLineCostChange(line.id, e.target.value)
                              }
                              placeholder={t("inventory.consideredCostPerUnit")}
                              disabled={!line.productId}
                              className={cn(
                                "w-full h-10 pl-6 pr-2 rounded-lg border bg-white text-xs font-semibold focus:border-accent focus:outline-hidden disabled:bg-gray-50",
                                formErrors.lines?.[index]
                                  ? "border-rose-500"
                                  : "border-(--color-text-border)"
                              )}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          {line.productId ? (
                            <div>
                              <div className="text-base font-extrabold text-foreground font-mono leading-tight">
                                {line.availableQty}
                              </div>
                              {line.lineEligibility ? (
                                <div
                                  className={cn(
                                    "text-[11px] font-semibold mt-0.5 capitalize",
                                    line.lineEligibility === "eligible" &&
                                      "text-emerald-600",
                                    line.lineEligibility === "shortfall" &&
                                      "text-amber-600",
                                    line.lineEligibility === "blocked" &&
                                      "text-rose-600"
                                  )}
                                >
                                  {eligibilityLabel(line.lineEligibility)}
                                </div>
                              ) : (
                                <div className="text-[10px] text-gray-400 font-medium mt-0.5">
                                  {t("inventory.allLayers")}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-xs font-semibold text-gray-600 font-mono pt-5">
                          {line.productId ? line.partNumber : "—"}
                        </td>
                        <td className="px-4 py-3 text-right pt-5">
                          <button
                            type="button"
                            onClick={() => handleRemoveLine(line.id)}
                            className="text-xs font-bold text-primary hover:underline bg-transparent border-0 cursor-pointer"
                          >
                            {t("formActions.remove")}
                          </button>
                        </td>
                      </tr>

                      {formErrors.lines?.[index] && (
                        <tr>
                          <td
                            colSpan={8}
                            className="px-8 pb-2 text-[11px] text-rose-500 font-bold"
                          >
                            ⚠️ {formErrors.lines[index]}
                          </td>
                        </tr>
                      )}

                      {line.isCollapsed && (
                        <tr className="border-b border-(--color-text-border)/40">
                          <td colSpan={8} className="px-12 pb-4 pt-1">
                            <div className="flex flex-wrap items-center gap-3 text-xs">
                              {line.lineEligibility === "blocked" ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                  {t("inventory.blockedByPrice")}
                                </span>
                              ) : line.shortfall > 0 ||
                                line.lineEligibility === "shortfall" ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                  {t("inventory.statusShortfall")}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  {t("inventory.statusAllocated")}
                                </span>
                              )}
                              <span className="text-gray-600 font-semibold">
                                {line.allocatedQty > 0
                                  ? t("inventory.allocatedAcrossBatches", {
                                      qty: line.allocatedQty,
                                      count: line.batchEntries.length,
                                    })
                                  : line.lineEligibility === "blocked"
                                    ? t("inventory.blockedByPrice")
                                    : t("inventory.shortfallQtyLabel", {
                                        qty: line.shortfall || line.iofQty,
                                      })}
                              </span>
                              {line.shortfall > 0 && line.allocatedQty > 0 && (
                                <span className="text-rose-600 font-bold">
                                  {t("inventory.shortfallQtyLabel", {
                                    qty: line.shortfall,
                                  })}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  setLines((prev) =>
                                    prev.map((l) => {
                                      if (l.id !== line.id) return l;
                                      const validKeys = new Set(
                                        (l.batches || []).map((b) => b.key)
                                      );
                                      // Keep only keys the user actually selected that still exist
                                      const nextSelected = (
                                        l.selectedBatchKeys || []
                                      ).filter((k) => validKeys.has(k));
                                      return {
                                        ...l,
                                        editingBatches: true,
                                        selectedBatchKeys: nextSelected,
                                      };
                                    })
                                  )
                                }
                                className="text-primary font-bold hover:underline bg-transparent border-0 cursor-pointer"
                              >
                                {t("inventory.changeBatches")}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}

                      {line.productId && !line.isCollapsed && (
                        <tr className="border-b border-(--color-text-border)/40">
                          <td colSpan={8} className="px-4 pb-5 pt-1">
                            <div className="rounded-xl border border-sky-100 bg-sky-50/40 overflow-hidden">
                              <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3 border-b border-sky-100/80">
                                <div>
                                  <h4 className="text-[11px] font-extrabold uppercase tracking-wider text-sky-900">
                                    {t("inventory.availableBatches")}
                                  </h4>
                                  <p className="text-[11px] text-sky-700/80 font-medium mt-0.5">
                                    {t("inventory.enterConsideredCostHint")}
                                  </p>
                                </div>
                              </div>

                              {line.loadingBatches ? (
                                <div className="px-4 py-6 text-xs text-gray-500 font-semibold animate-pulse">
                                  {t("inventory.autoAllocating")}
                                </div>
                              ) : line.sortedBatches.length === 0 ? (
                                <div className="px-4 py-6 space-y-2">
                                  <div className="text-xs text-rose-500 font-semibold">
                                    {t("inventory.noAvailableBatches")}
                                  </div>
                                  {line.lineEligibility === "shortfall" && (
                                    <div className="text-[11px] text-rose-700 bg-rose-50 px-3 py-2 rounded-lg border border-rose-100 font-bold">
                                      {t("inventory.shortfallQtyLabel", {
                                        qty: line.shortfall || line.iofQty,
                                      })}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="overflow-x-auto">
                                  {line.lineEligibility === "shortfall" && (
                                    <div className="mx-4 mt-3 mb-1 text-[11px] text-rose-700 bg-rose-50 px-3 py-2 rounded-lg border border-rose-100 font-bold">
                                      {t("inventory.shortfallQtyLabel", {
                                        qty:
                                          line.shortfall ||
                                          Math.max(
                                            0,
                                            (Number(line.iofQty) || 0) -
                                              (Number(line.priceEligibleQty) ||
                                                0)
                                          ),
                                      })}
                                    </div>
                                  )}
                                  <ClientTable className="w-full text-left">
                                    <thead>
                                      <tr className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400">
                                        <th className="px-4 py-2 w-10" />
                                        <th className="px-3 py-2">
                                          {t("inventory.pricePerUnit")}
                                        </th>
                                        <th className="px-3 py-2">
                                          {t("inventory.quantity")}
                                        </th>
                                        <th className="px-3 py-2">
                                          {t("inventory.batch")}
                                        </th>
                                        <th className="px-3 py-2">
                                          {t("inventory.warehouse")}
                                        </th>
                                        <th className="px-3 py-2">
                                          {t("dateTime.receiptDate")}
                                        </th>
                                        <th className="px-3 py-2">
                                          {t("inventory.warrantyEnd")}
                                        </th>
                                        <th className="px-3 py-2">
                                          {t("inventory.eligibility")}
                                        </th>
                                        <th className="px-3 py-2 text-right">
                                          {t("inventory.allocatedQty")}
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {line.sortedBatches.map((batch) => {
                                        const isEligible =
                                          batch.eligibility === "eligible";
                                        const checked =
                                          line.selectedBatchKeys.includes(
                                            batch.key
                                          );
                                        const allocatedRaw =
                                          line.batchQtyByKey?.[batch.key];
                                        const allocatedDisplay =
                                          allocatedRaw === "" ||
                                          allocatedRaw == null
                                            ? ""
                                            : String(allocatedRaw);
                                        return (
                                          <tr
                                            key={batch.key}
                                            className={cn(
                                              "border-t border-sky-100/70 text-xs",
                                              checked && "bg-white/70"
                                            )}
                                          >
                                            <td className="px-4 py-2.5">
                                              <input
                                                type="checkbox"
                                                key={`${line.id}-${batch.key}-cb`}
                                                checked={checked}
                                                disabled={!isEligible}
                                                onChange={() =>
                                                  handleToggleBatch(
                                                    line.id,
                                                    batch.key,
                                                    isEligible
                                                  )
                                                }
                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-accent disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                                              />
                                            </td>
                                            <td className="px-3 py-2.5 font-mono font-semibold text-foreground">
                                              {formatCurrency(
                                                batch.purchasePricePerUnit
                                              )}
                                            </td>
                                            <td className="px-3 py-2.5 font-mono font-semibold">
                                              {batch.availableQty}
                                            </td>
                                            <td className="px-3 py-2.5 font-mono font-bold">
                                              {batch.batchNumber}
                                            </td>
                                            <td className="px-3 py-2.5 font-semibold text-gray-600">
                                              {batch.warehouse}
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-600">
                                              {formatDisplayDate(
                                                batch.receiptDate
                                              )}
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-600">
                                              {formatDisplayDate(
                                                batch.warrantyEndDate
                                              )}
                                            </td>
                                            <td className="px-3 py-2.5">
                                              <div className="flex flex-col items-start gap-1.5">
                                                {batch.unlockStatus ===
                                                "REJECTED" ? (
                                                  <span className="font-extrabold text-[11px] uppercase tracking-wider text-rose-700">
                                                    {t(
                                                      "inventory.revokeUnlockStatusRejected"
                                                    )}
                                                  </span>
                                                ) : null}
                                                <span
                                                  className={cn(
                                                    "font-semibold capitalize",
                                                    batch.eligibility ===
                                                      "eligible" &&
                                                      "text-emerald-600",
                                                    batch.eligibility ===
                                                      "blocked" &&
                                                      "text-rose-600",
                                                    batch.eligibility ===
                                                      "awaiting" &&
                                                      "text-gray-400"
                                                  )}
                                                >
                                                  {eligibilityLabel(
                                                    batch.eligibility
                                                  )}
                                                </span>
                                                {batch.unlockStatus ===
                                                  "PENDING" ||
                                                batch.unlockStatus ===
                                                  "QUEUED" ? (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider border bg-amber-50 text-amber-700 border-amber-200">
                                                    {t(
                                                      "inventory.revokeUnlockStatusPending"
                                                    )}
                                                  </span>
                                                ) : null}
                                                {batch.eligibility ===
                                                  "blocked" &&
                                                batch.unlockStatus !==
                                                  "PENDING" &&
                                                batch.unlockStatus !==
                                                  "QUEUED" &&
                                                batch.unlockStatus !==
                                                  "REJECTED" ? (
                                                  <button
                                                    type="button"
                                                    disabled={
                                                      actionSubmitting ||
                                                      submitting
                                                    }
                                                    onClick={() =>
                                                      openUnlockRequest(
                                                        line,
                                                        batch
                                                      )
                                                    }
                                                    className="h-7 px-2.5 rounded-lg border border-sky-200 bg-white text-[10px] font-extrabold uppercase tracking-wider text-sky-700 hover:bg-sky-50 transition cursor-pointer disabled:opacity-50"
                                                  >
                                                    {t("formActions.unlock")}
                                                  </button>
                                                ) : null}
                                              </div>
                                            </td>
                                            <td className="px-3 py-2.5 text-right">
                                              {checked ? (
                                                <input
                                                  type="number"
                                                  min="0"
                                                  step="1"
                                                  max={
                                                    Number(batch.availableQty) ||
                                                    undefined
                                                  }
                                                  value={allocatedDisplay}
                                                  onChange={(e) =>
                                                    handleBatchAllocatedQtyChange(
                                                      line.id,
                                                      batch.key,
                                                      e.target.value
                                                    )
                                                  }
                                                  className="w-[88px] h-8 ml-auto px-2 rounded-lg border border-(--color-text-border) bg-white text-xs font-bold font-mono text-primary text-right focus:border-accent focus:outline-hidden"
                                                />
                                              ) : (
                                                <span className="font-mono font-bold text-gray-400">
                                                  —
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </ClientTable>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </ClientTable>
            </div>

            <div className="p-4 sm:px-6 sm:pb-6 flex items-center justify-between flex-wrap gap-3 border-t border-(--color-text-border)/40">
              <button
                type="button"
                onClick={handleAddLine}
                className="inline-flex items-center h-9 px-4 rounded-xl border border-(--color-text-border) bg-white text-xs font-bold text-foreground hover:bg-gray-50 transition cursor-pointer"
              >
                {t("inventory.addLine")}
              </button>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="h-10 px-5 rounded-xl border border-(--color-text-border) per-file-hint font-bold text-xs hover:bg-gray-50 transition cursor-pointer"
                >
                  {t("formActions.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="h-10 px-6 rounded-xl bg-primary hover:bg-accent text-white font-bold text-xs transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting
                    ? t("inventory.saving")
                    : t("formActions.saveAllocation")}
                </button>
              </div>
            </div>
          </ContentCard>
        </form>
      )}
    </AppLayout>
  );
}
